<script>
  import { beforeUpdate } from 'svelte';
  import GanttHelper from './GanttHelper.js';
  import OrderGanttBarUnit from './OrderGanttBarUnit.svelte'
  import { SelectRectangle } from "../../utils/selectRectangle";
  import { getTimeStringSec, roundToNearestTime } from '../../utils/time.js';
  import { translate } from '../../i18n/i18next.js';

  export let GanttStore
    export let ID;
    export let Height;
    export let OriginY;
    export let Order;
    export let Data;
    export let StartDate;
    export let EndDate;
    export let AdjustStamp;
    export let IsOperationBarCoveredOnOrderBar; // Whether is operation bar covered on order bar 
    export let IsOperation; // whether is operation bar unfolded in a separate line
    export let IsResult = false; // whether is a result bar 
    export let StripeTimeStamp = null; // stripe
    /* locs to draw connection lines. Format in like 
    {
    "left": [
        [
            -1,
            "2023-06-16T02:39:00.000Z"
        ]
    ],
    "right": []
    } */

    export let ganttChartType = GanttStore.GanttChartTypes.OrderGantt;

    const {
      TimelineItems,
      CurrentStyleIndex
    } = GanttStore;

    $: simple = (ganttChartType == GanttStore.GanttChartTypes.SimpleOrderGantt || ganttChartType == GanttStore.GanttChartTypes.ExpectedActualCmp); // whether it is simple gantt chart bar;
    $: eac = (ganttChartType == GanttStore.GanttChartTypes.ExpectedActualCmp);

    $: adjustedHeight = Math.min(Math.max(Height / 2, 1), 5);
    $: adjustedY = OriginY + Height - adjustedHeight;
    $: adjustedLET = CalculateStampX(Data._let)
    $: adjustedEST = CalculateStampX(Data._est)
  
    $: Selected = $TimelineItems.some((el) => el[0] == ID);
    let optimizationBarString = 10;
    let optimizationBarBorder = 30;
    let optimizationShapeSimplify = 50;
    let displayedRowCount = 5;

    $: start = null;
    $: end = null;

    $: {
      if (IsResult && Data._resultStart && (Data._resultEnd || Data._resultProgress || Data._resultQty)) {
        start = new Date(Data._resultStart);
        end = new Date(Data._resultEnd);

        if (!Data._resultEnd) {
          const timeCost = Data._productionArr[Data._productionArr.length - 1].getTime() - Data._productionArr[0].getTime();
          let percentage = 0;
          if (Data._resultProgress) {
            percentage = Data._resultProgress / 100;
          }
          if (Data._resultQty) {
            percentage = Math.max(percentage, Data._resultQty / Data._outputItemQty);
          }
          if (percentage > 0)
            end =  new Date(start.getTime() + percentage * timeCost);
        }
      } else {
        if (Data._productionArr.length >= 2) {
          start = Data._productionArr[0];
          end = Data._productionArr[Data._productionArr.length - 1];
        }
      }
      if (simple) {
        start = roundToNearestTime(start, 1, true);
        let roundedEnd = roundToNearestTime(end, 1);
        // If the end date is in the same day as LET, do not use rounded end time
        if (!Data._let || end.toDateString() != Data._let.toDateString()) {
          end = roundedEnd;
        }
      }
    }
  
    const CalculateStampX = (stamp) => Math.max(0, Math.min(100, 100 * GanttHelper._gantHelper_scale(AdjustStamp(stamp).getTime(), EndDate.getTime(), StartDate.getTime())));
  
    let hbsrtunsub = () => {};
    let bssrtunsub = () => {};
    let hbbrtunsub = () => {};
    let drcunsub = () => {};

    beforeUpdate(() => {
      hbsrtunsub();
      bssrtunsub();
      hbbrtunsub();
      drcunsub();
  
      Selected = SelectRectangle.IsSelectedId(ID.toString());
      hbsrtunsub = GanttStore.OPTIMIZATION_HideBarStringRowThreshold.subscribe((hbsrt) => optimizationBarString = hbsrt);
      bssrtunsub = GanttStore.OPTIMIZATION_BarSimplifyShapeRowThreshold.subscribe((bssrt) => optimizationShapeSimplify = bssrt);
      bssrtunsub = GanttStore.OPTIMIZATION_HideBarBorderRowThreshold.subscribe((hbbrt) => optimizationBarBorder = hbbrt);
      hbbrtunsub = GanttStore.DisplayedRowCount.subscribe((drc) => displayedRowCount = drc);
    });
  
  </script>

  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <g
  class={"o_" + Order + " selectable orderganttbar" }
    id={ID}
    on:click={SelectRectangle.SingleClickEventWrapperPassive()}
  >
     {#if !simple && !IsResult && Data?._setupArr?.length >= 2}
      <OrderGanttBarUnit
        Timestamps={Data._setupArr}
        Color="#999999"
        StartYMain={OriginY+Height*0.325}
        HeightYMain={Height*0.35}
        StartDate={StartDate}
        EndDate={EndDate}
        HiddenBarBorders={true}
        SimplifiedShape={displayedRowCount >= optimizationShapeSimplify}
        AdjustStamp={AdjustStamp}
        Transparent={displayedRowCount >= optimizationBarBorder}
      />
    {/if}
  
    {#if !simple && !IsResult && Data?._teardownArr?.length >= 2}
      <OrderGanttBarUnit
        Timestamps={Data._teardownArr}
        Color="#999999"
        StartYMain={OriginY+Height*0.325}
        HeightYMain={Height*0.35}
        StartDate={StartDate}
        EndDate={EndDate}
        HiddenBarBorders={true}
        SimplifiedShape={displayedRowCount >= optimizationShapeSimplify}
        AdjustStamp={AdjustStamp}
        Transparent={displayedRowCount >= optimizationBarBorder}
      />
    {/if}
  
    {#if StripeTimeStamp}
      <OrderGanttBarUnit
        Timestamps={[start, roundToNearestTime(StripeTimeStamp, 1)]}
        Color="url(#stripe)"
        StartYMain={OriginY+Height*0.125}
        HeightYMain={Height*0.75}
        StartDate={StartDate}
        EndDate={EndDate}
        HiddenBarBorders={true}
        SimplifiedShape={true}
        AdjustStamp={AdjustStamp}
        Transparent={false}
    />
    {:else if start && end}
    <!-- Delay happens, do not use simplified productionArr -->
      {#if Data._let && end > Data._let} 
        <!-- There exist a part of bar before the LET -->
        {#if start < Data._let}
          <OrderGanttBarUnit
            Timestamps={[start, Data._let]}
            Color={simple? "#7dc4ff" : Data._barColors[$CurrentStyleIndex]}
            StartYMain={!eac ? OriginY+Height*0.125 : OriginY+Height*0.375}
            HeightYMain={!eac ? Height*0.75 : Height*0.25}
            StartDate={StartDate}
            EndDate={EndDate}
            HiddenBarBorders={displayedRowCount >= optimizationBarBorder}
            SimplifiedShape={true}
            AdjustStamp={AdjustStamp}
            Selected={Selected && !simple}
          />
        {/if}
        <!-- Note the case that all of the bar is later than the LET -->
        <OrderGanttBarUnit
          Timestamps={[Data._let >= start ? Data._let : start, end]}
          Color="#ff7575"
          StartYMain={!eac ? OriginY+Height*0.125 : OriginY+Height*0.375}
          HeightYMain={!eac ? Height*0.75 : Height*0.25}
          StartDate={StartDate}
          EndDate={EndDate}
          HiddenBarBorders={displayedRowCount >= optimizationBarBorder}
          SimplifiedShape={true}
          AdjustStamp={AdjustStamp}
          Transparent={false}
          Selected={Selected && !simple}
        />
      {:else}
        {#if IsOperationBarCoveredOnOrderBar || IsOperation} 
          <OrderGanttBarUnit
            Timestamps={[start, end]}
            Color={simple? "#7dc4ff" : Data._barColors[$CurrentStyleIndex]}
            StartYMain={OriginY+Height*0.2}
            HeightYMain={Height*0.6}
            StartDate={StartDate}
            EndDate={EndDate}
            HiddenBarBorders={displayedRowCount >= optimizationBarBorder}
            SimplifiedShape={true}
            AdjustStamp={AdjustStamp}
            Transparent={!IsOperation}
            Selected={Selected && !simple}
          />

          {#if (Data._resStatus === "B" || (Data._resStatus === "T" && (Data._resultProgress || Data._resultQty)))}
          <OrderGanttBarUnit
              Timestamps={Data._resStatus === "B" ? [start, end] : [start, new Date(start.getTime() + (end.getTime() - start.getTime()) * Math.max((Data._resultProgress ? Data._resultProgress / 100: 0), (Data._resultQty ? Data._resultQty : 0) / Data._outputItemQty))]}
              Color="url(#stripe)"
              StartYMain={OriginY+Height*0.2}
              HeightYMain={Height*0.6}
              StartDate={StartDate}
              EndDate={EndDate}
              HiddenBarBorders={displayedRowCount >= optimizationBarBorder}
              SimplifiedShape={true}
              AdjustStamp={AdjustStamp}
              Transparent={false}
              Selected={Selected && !simple}
            />
          {/if}

        {:else}
        <!-- Do not use simplified arr for eac -->
          <OrderGanttBarUnit
            Timestamps={[start, end]}
            Color={simple? "#7dc4ff" : Data._barColors[$CurrentStyleIndex]}
            StartYMain={!eac ? OriginY+Height*0.125 : OriginY+Height*0.375}
            HeightYMain={!eac ? Height*0.75 : Height*0.25}
            StartDate={StartDate}
            EndDate={EndDate}
            HiddenBarBorders={displayedRowCount >= optimizationBarBorder}
            SimplifiedShape={true}
            AdjustStamp={AdjustStamp}
            Transparent={false}
            Selected={Selected && !simple}
          />
        {/if}
      {/if}
    {/if}
  
    {#if eac && Data._storedStartTime &&  Data._storedEndTime}
    <!-- Do not use simplified productionArr -->
      {#if Data._let && Data._storedEndTime > Data._let} 
        <!-- There exist a part of bar before the LET -->
        {#if Data._storedStartTime < Data._let}
          <OrderGanttBarUnit
            Timestamps={[Data._storedStartTime, Data._let]}
            Color={"#4fe89e"}
            StartYMain={OriginY+Height*0.1}
            HeightYMain={Height*0.25}
            StartDate={StartDate}
            EndDate={EndDate}
            HiddenBarBorders={displayedRowCount >= optimizationBarBorder}
            SimplifiedShape={true}
            AdjustStamp={AdjustStamp}
            Transparent={false}
          />
        {/if}
        <!-- Note the case that all of the bar is later than the LET -->
        <OrderGanttBarUnit
          Timestamps={[Data._let >= Data._storedStartTime ? Data._let : Data._storedStartTime, Data._storedEndTime]}
          Color={"#4fe89e"}
          StartYMain={OriginY+Height*0.1}
          HeightYMain={Height*0.25}
          StartDate={StartDate}
          EndDate={EndDate}
          HiddenBarBorders={displayedRowCount >= optimizationBarBorder}
          SimplifiedShape={true}
          AdjustStamp={AdjustStamp}
          Transparent={false}
        />
      {:else}
        <OrderGanttBarUnit
          Timestamps={[Data._storedStartTime, Data._storedEndTime]}
          Color={"#4fe89e"}
          StartYMain={OriginY+Height*0.1}
          HeightYMain={Height*0.25}
          StartDate={StartDate}
          EndDate={EndDate}
          HiddenBarBorders={displayedRowCount >= optimizationBarBorder}
          SimplifiedShape={true}
          AdjustStamp={AdjustStamp}
          Transparent={false}
        />
      {/if}
    {/if}

    {#if eac && Data._resultStartTime &&  Data._resultEndTime}
      <!-- Do not use simplified productionArr -->
      {#if Data._let && Data._resultEndTime > Data._let} 
        <!-- There exist a part of bar before the LET -->
        {#if Data._resultStartTime < Data._let}
          <OrderGanttBarUnit
            Timestamps={[Data._resultStartTime, Data._let]}
            Color={"#ffb21c"}
            StartYMain={OriginY+Height*0.65}
            HeightYMain={Height*0.25}
            StartDate={StartDate}
            EndDate={EndDate}
            HiddenBarBorders={displayedRowCount >= optimizationBarBorder}
            SimplifiedShape={true}
            AdjustStamp={AdjustStamp}
            Transparent={false}
          />
        {/if}
        <!-- Note the case that all of the bar is later than the LET -->
        <OrderGanttBarUnit
          Timestamps={[Data._let >= Data._resultStartTime ? Data._let : Data._resultStartTime, Data._resultEndTime]}
          Color={"#ffb21c"}
          StartYMain={OriginY+Height*0.65}
          HeightYMain={Height*0.25}
          StartDate={StartDate}
          EndDate={EndDate}
          HiddenBarBorders={displayedRowCount >= optimizationBarBorder}
          SimplifiedShape={true}
          AdjustStamp={AdjustStamp}
          Transparent={false}
        />
      {:else}
        <OrderGanttBarUnit
          Timestamps={[Data._resultStartTime, Data._resultEndTime]}
          Color={"#ffb21c"}
          StartYMain={OriginY+Height*0.65}
          HeightYMain={Height*0.25}
          StartDate={StartDate}
          EndDate={EndDate}
          HiddenBarBorders={displayedRowCount >= optimizationBarBorder}
          SimplifiedShape={true}
          AdjustStamp={AdjustStamp}
          Transparent={false}
        />
      {/if}
    {/if}

    <title>
      {Data._hoverTooltip[$CurrentStyleIndex]}
    </title>
  
    {#if (ganttChartType == GanttStore.GanttChartTypes.OrderGantt  && (!IsOperationBarCoveredOnOrderBar || IsOperation)) || (ganttChartType == GanttStore.GanttChartTypes.PeggedOrderGantt && IsOperation) && displayedRowCount < 1.5 * optimizationBarString && end}
      <!-- Make text at the right side -->
      <text
        fill={Data?._operationBarRightTextColor?.[$CurrentStyleIndex] || Data?._orderBarRightTextColor?.[$CurrentStyleIndex] || ((Data._late) ? "red" : "black")}
        font-size="{Math.min(20 / displayedRowCount, 2)}vh"
        x="{CalculateStampX(end)}%"
        y="{ OriginY+Height*0.4}%"
        text-anchor="start"
        dominant-baseline="hanging"
        pointer-events="none"
      >
      {#each Data._taskText[$CurrentStyleIndex].split("\n") as text, idx}
        <tspan
          x="{CalculateStampX(end)}%"
          dy="{(idx==0) ? 0 : (10 / displayedRowCount)}vh"
        >
          {text}
        </tspan>
        {/each}
      </text>
    {/if}

    {#if Data?._operationBarInsideText?.[$CurrentStyleIndex] && ((ganttChartType == GanttStore.GanttChartTypes.OrderGantt && (IsOperationBarCoveredOnOrderBar || IsOperation)) || (ganttChartType == GanttStore.GanttChartTypes.PeggedOrderGantt && IsOperation)) && displayedRowCount < 1.5 * optimizationBarString && start}
    <defs>
      <clipPath id="text-clip-{Data._objectID}-{IsOperationBarCoveredOnOrderBar}">
        <rect 
          x="{CalculateStampX(start)}%" 
          y="{OriginY + Height * 0.2}%" 
          width="{CalculateStampX(end) - CalculateStampX(start)}%"
          height="{Height}%"
        />
      </clipPath>
    </defs>
  
    <text
      fill="{Data?._operationBarInsideTextColor?.[$CurrentStyleIndex] || 'black'}"
      font-size="{Math.min(20 / displayedRowCount, 2)}vh"
      x="{CalculateStampX(start)}%"
      y="{OriginY + Height * 0.25}%" 
      text-anchor="start"
      dominant-baseline="hanging"
      pointer-events="none"
      clip-path="url(#text-clip-{Data._objectID}-{IsOperationBarCoveredOnOrderBar})"
    >
      {#each Data._operationBarInsideText[$CurrentStyleIndex].split("\n") as text, idx}
        <tspan
          x="{CalculateStampX(start)}%"
          dy="{(idx == 0) ? 0 : (15 / displayedRowCount)}vh"
        >
          {text}
        </tspan>
      {/each}
    </text>
    {/if}
  
  </g>

  {#if Data._let && CalculateStampX(Data._let) != 0 && CalculateStampX(Data._let) != 100}
    <g>
      <use
        x="{adjustedLET}%"
        y="{adjustedY}%"
        href="#marker_let"
        height="{adjustedHeight}%"
      />
      <title>
        {translate("asprova.terms.due")}: {getTimeStringSec(Data._let)}
      </title>
    </g>
  {/if}

  {#if Data._let && CalculateStampX(Data._est) != 0 && CalculateStampX(Data._est) != 100}
    <g>
      <use
        x="{adjustedEST}%"
        y="{adjustedY}%"
        href="#marker_let"
        height="{adjustedHeight}%"
      />
      <title>
        {translate("asprova.terms.est")}: {getTimeStringSec(Data._est)}
      </title>
    </g>
  {/if}

  <style>
    g.orderganttbar {
      outline: solid 4px hsla(0, 0%, 0%, 0);
      outline-offset: -2px;
    }
  
  </style>
  